import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TradingChart from '../components/TradingChart';
import { ChevronLeftIcon, RefreshIcon } from '../components/SVGIcons';
import { useTheme } from '../context/ThemeContext';
import { apiServices } from '../utils/apiServices';
import { ISubnetDetailResponse, IStakeResponse, ITraderResponse } from '../utils/types';
import SubnetDetailOverview from '../components/subnet/SubnetDetailOverview';
import { useAppContext } from '../context/AppContext';
import TraderStakesHistory from '../components/trader/TraderStakesHistory';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import SubnetUserInfo from '../components/subnet/SubnetUserInfo';
import AddAndWithdraw from '../components/subnet/AddAndWithdraw';
import TradePanels from '../components/subnet/TradePanels';
import UnstakeConfirmModal from '../components/trader/UnstakeConfirmModal';
import TraderAddWithdrawCollateralModal from '../components/trader/TraderAddWithdrawCollateralModal';
import { useEVMContext } from '../context/EVMContext';
import { ethers } from 'ethers';
import { parseContractEvents } from '../utils';

const SubnetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [subnet, setSubnet] = useState<ISubnetDetailResponse | null>(null);
  const { signer, accounts, poolContract, provider } = useEVMContext();
  const [traderData, setTraderData] = useState<ITraderResponse | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { setIsLoading } = useAppContext();
  const [balance, setBalance] = useState<string>('');
  const [isUnstakeConfirmModalOpen, setIsUnstakeConfirmModalOpen] = useState(false);
  const [activeStake, setActiveStake] = useState<IStakeResponse | null>(null);
  const [actionType, setActionType] = useState<'add' | 'withdraw'>('add');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [amount, setAmount] = useState('');

  const updateBalance = useCallback(async() => {
    if (!(window as any).ethereum) {
      throw new Error('No EVM wallet detected');
    }
    const connectedWalletAddress = await signer?.getAddress();
    if (connectedWalletAddress && provider) {
      const balance = await provider.getBalance(connectedWalletAddress);
      console.log('EVM Balance:', ethers.formatEther(balance));
      // Convert balance from wei to ether
      setBalance(ethers.formatEther(balance));
    }
  }, [signer]);
  
  useEffect(() => {
    updateBalance();
  }, [updateBalance]);

  const refreshSubnetDetailData = useCallback(() => {
    getTraderData();
    fetchSubnet();
  }, [id]);

  const fetchSubnet = async () => {
    if (!id) return;
    const subnet = await apiServices.price.subnet(id);
    setSubnet(subnet);
  };

  useEffect(() => {
    fetchSubnet();
  }, [id]);

  const handleUnstakeAllSubmit = () => {
    if (activeStake) {
      handleUnstakeAll(activeStake)
    }
  }

  const handleUnstakeAll = async (stake: IStakeResponse) => {
    if (!id) {
      toast.error('Subnet ID is not defined');
      return;
    }
    if (!poolContract || !signer) {
      toast.error('EVM Wallet is not connected');
      return;
    }
    setIsLoading(true);
    const connectedWalletAddress = await signer?.getAddress();

    try {
      poolContract.unstakeAllFromSubnet(
        stake.subnet,
      ).then((tx: any) => {
        return tx.wait();
      }).then(async (receipt: any) => {
        // Parse event from logs
        parseContractEvents(receipt, poolContract);
        toast.success('Unstake successful!');

        await apiServices.pool.refresh(connectedWalletAddress, false);
        getTraderData();
      }).catch((error: any) => {
        console.error('Error while deposits:', error);
        toast.error(`Failed to deposit: ${error.message || 'Please try again.'}`);
      });
    } catch (error) {
      console.log(`Failed to stake:`, error);
      toast.error(`Failed to stake. Please try again.`);
    }
    setIsLoading(false);
  };

  const getTraderData = async () => {
    if (!accounts[0]) return;
    // setIsLoading(true);
    try {
      setIsRefreshing(true);
      const res = await apiServices.traders.getById(accounts[0]);
      setTraderData({
        ...res,
        stakes: res.stakes?.filter((stake: IStakeResponse) => stake.subnet === Number(id)) || []
      });
    } catch (err) {
      console.error('Failed to fetch trader details:', err);
      setTraderData(null);
    } finally {
      // setIsLoading(false);
      setIsRefreshing(false);
    }
  }

  const handleAddCollateral = async () => {
    setActionType('add');
    setIsModalOpen(true);
  };

  const handleWithdraw = async () => {
    setActionType('withdraw');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAmount('');
  };

  const handleSubmit = async () => {
    if (!accounts[0]) {
      toast.error('Trader ID is not defined');
      return;
    }
    try {
      if (!poolContract || !signer) {
        toast.error('EVM Wallet is not connected');
        return;
      }
      setIsLoading(true);
      const connectedWalletAddress = await signer?.getAddress();
      if (actionType === 'add') {
        console.log('Adding collateral:', amount);
        poolContract.addCollateral({
          value: ethers.parseEther(amount)
        }).then((tx: any) => {
          return tx.wait();
        }).then(async (receipt: any) => {
          // Parse LiquidityAdded event from logs
          parseContractEvents(receipt, poolContract);
          toast.success('Collateral added successfully!');
          // TODO: Refresh trader data from the backend

          const res = await apiServices.pool.refresh(connectedWalletAddress, true);
          setTraderData(res as ITraderResponse);
          updateBalance();
        }).catch((error: any) => {
          console.error('Error adding collateral:', error);
          toast.error(`Failed to add collateral: ${error.message || 'Please try again.'}`);
        });
      } else {
        poolContract.removeCollateral(ethers.parseEther(amount)).then((tx: any) => {
          return tx.wait();
        }).then(async (receipt: any) => {
          // Parse LiquidityAdded event from logs
          parseContractEvents(receipt, poolContract);
          toast.success('Collateral withdrawn successfully!');

          const res = await apiServices.pool.refresh(connectedWalletAddress, true);
          setTraderData(res as ITraderResponse);
          updateBalance();
        }).catch((error: any) => {
          console.error('Error withdrawing collateral:', error);
          toast.error(`Failed to withdraw collateral: ${error.message || 'Please try again.'}`);
        });
      }
    } catch (error) {
      console.log(`Failed to ${actionType} collateral:`, error);
      toast.error(`Failed to ${actionType} collateral: Please try again.`);
      setIsLoading(false);
    } finally {
      setIsModalOpen(false);
      setAmount('');
    }
  };

  useEffect(() => {
    if (!accounts[0]) return;
    getTraderData();
    // const interval = setInterval(() => {
    //   if (activeAccount?.address) {
    //     getTraderData();
    //   }
    // }, 60000); // 1 minute in milliseconds

    // return () => clearInterval(interval); // Cleanup on unmount
  }, [accounts]);

  return <div className="min-h-screen p-4 sm:p-6 lg:p-8">
    <div className="max-w-7xl mx-auto">
      <div className='w-full flex justify-between items-center mb-[40px]'>
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="rounded-[100px] p-2 bg-gray-200 dark:bg-gray-800 mr-4"
          >
            <ChevronLeftIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
          </button>
          <h1 className={clsx(
            "text-2xl font-borna text-black",
            "sm:text-[32px]",
            "dark:text-white"
          )}>{subnet?.name} [{id}]</h1>
        </div>
        <div className='flex items-center'>
          <AddAndWithdraw handleAddCollateral={handleAddCollateral} handleWithdraw={handleWithdraw} />
          <button
            onClick={refreshSubnetDetailData}
            className="p-2 bg-[#F2F2F2] hover:opacity-80 dark:bg-[#262626] text-black dark:text-white rounded-[100px] transition-opacity text-[14px] w-[40px] sm:w-[46px] h-[40px] sm:h-[46px] flex items-center justify-center ml-4"
          >
            <span className={`${isRefreshing ? 'animate-spin' : ''}`}>
              <RefreshIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
            </span>
          </button>
        </div>
      </div>

      <div className="flex gap-[56px]">
        <div className='w-[256px]'>
          {accounts[0] && <SubnetUserInfo traderData={traderData} balance={balance} />}
          <SubnetDetailOverview subnet={subnet} />
        </div>
        <div className='flex-1'>
          <div className="w-full h-auto mb-[24px]">
            <TradingChart />
          </div>
          <TradePanels refreshFunc={getTraderData} subnetInfo={subnet} traderData={traderData} />
          {traderData && <TraderStakesHistory traderData={traderData} setIsUnstakeConfirmModalOpen={setIsUnstakeConfirmModalOpen} setActiveStake={setActiveStake} />}
        </div>
      </div>
    </div>

    {isModalOpen && (
      <TraderAddWithdrawCollateralModal actionType={actionType} handleCloseModal={handleCloseModal} handleSubmit={handleSubmit} amount={amount} setAmount={setAmount} />
    )}

    {isUnstakeConfirmModalOpen && (
      <UnstakeConfirmModal handleSubmit={handleUnstakeAllSubmit} handleCloseModal={() => setIsUnstakeConfirmModalOpen(false)} />
    )}
  </div>;
};

export default SubnetDetailPage;